import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", type: "customer", contact: "", notes: "" };

export default function Parties() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    const r = await api.get("/parties");
    setItems(r.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, type: it.type, contact: it.contact || "", notes: it.notes || "" }); setOpen(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/parties/${editing.id}`, form);
        toast.success("Party updated");
      } else {
        await api.post("/parties", form);
        toast.success("Party created");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    await api.delete(`/parties/${id}`);
    toast.success("Party deleted");
    fetchAll();
  };

  return (
    <div className="p-10 max-w-5xl" data-testid="parties-page">
      <PageHeader overline="Contacts" title="Parties & Accounts" testid="parties-header">
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-party-button"
            className="rounded-none bg-[#0F172A] hover:bg-[#1E293B] text-white h-10 text-xs uppercase tracking-wider font-semibold">
            + New Party
          </Button>
        )}
      </PageHeader>

      <div className="border border-[#E5E7EB] bg-white" data-testid="parties-list">
        {items.length === 0 && (
          <div className="px-6 py-16 text-center text-sm text-[#6B7280]" data-testid="parties-empty">
            No parties yet. Add customers, suppliers, or employees to track cash flow per relationship.
          </div>
        )}
        <table className="w-full text-sm">
          {items.length > 0 && (
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left px-4 py-3 overline">Name</th>
                <th className="text-left px-4 py-3 overline">Type</th>
                <th className="text-left px-4 py-3 overline">Contact</th>
                <th className="text-left px-4 py-3 overline">Notes</th>
                <th className="px-4 py-3 overline text-right">Actions</th>
              </tr>
            </thead>
          )}
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`party-row-${p.id}`}>
                <td className="px-4 py-3 font-medium text-[#111827]">{p.name}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563]">
                    {p.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#4B5563]">{p.contact || "—"}</td>
                <td className="px-4 py-3 text-[#6B7280] text-xs max-w-xs truncate">{p.notes || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin ? (
                      <>
                        <button onClick={() => openEdit(p)} data-testid={`edit-party-${p.id}`}
                          className="p-1.5 hover:bg-[#E5E7EB]"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => onDelete(p.id)} data-testid={`delete-party-${p.id}`}
                          className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <span className="text-xs text-[#9CA3AF]">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="party-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit Party" : "New Party"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              {editing ? "Update the contact details for this party." : "Add a customer, supplier, employee, or other party to track transactions against."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="party-form">
            <div>
              <Label className="overline mb-1.5 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                data-testid="party-name" className="rounded-none h-10" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="party-type" className="rounded-none h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="services">Services</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Contact</Label>
              <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                data-testid="party-contact" placeholder="Phone / Email" className="rounded-none h-10" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="party-notes" rows={2} className="rounded-none" />
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="party-cancel"
                className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
              <Button type="submit" data-testid="party-save"
                className="rounded-none h-10 bg-[#0F172A] hover:bg-[#1E293B] text-xs uppercase tracking-wider">
                {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
